import React, { useState } from 'react';

import _ from 'lodash';

import { Checkbox } from '@app/shadcn/components/ui/checkbox';
import CloseModal from '@Components/common/close-modal';
import CheckedCircle from '@Components/icons/checked-circle';
import { Disclosure } from '@headlessui/react';

import { ChevronDown } from '@app/components/icons/chevron-down';
import GoogleFormsImportNotice from '@app/components/form-integrations/google-forms-import-notice';
import environments from '@app/configs/environments';
import { useGoogleFormsImportMode } from '@app/lib/hooks/use-google-forms-import-mode';
import ConnectWithProviderButton from '@Components/login/login-with-google-button';

interface ImportErrorViewProps {
    provider: string;
    closable?: boolean;
    unauthorizedScopes?: Array<string>;
}

interface IScope {
    type?: string;
    name: string;
    url?: string;
}

interface IPermission {
    type?: string;
    name: string;
    icon?: React.ReactNode;
    isPermissionGiven: boolean;
    description?: string;
    scope?: string;
}

interface IDefaultContent {
    type: 'dark' | 'light';
    permissionText: string;
    permissions: Array<IPermission>;
}

export default function ImportErrorView({ provider, closable = true, unauthorizedScopes }: ImportErrorViewProps) {
    const [isConsentGiven, setIsConsentGiven] = useState(false);
    // Connecting a Google account only makes sense while imports are on: in
    // readonly and off the backend refuses it (403 / 410), so say why instead.
    const { mode } = useGoogleFormsImportMode();

    const googlePermissions: Array<IPermission> = [
        {
            type: 'non-sensitive',
            isPermissionGiven: false,
            name: 'Permission to search and pick Google Forms from Drive',
            description: 'To be able to show Google File Picker, we require permissions to search your Google Drive for Google Forms.',
            scope: 'https://www.googleapis.com/auth/drive.file'
        },
        {
            type: 'sensitive',
            isPermissionGiven: false,
            name: 'Permissions to import your Google Forms',
            description: 'To be able to import forms from Google, we require access permissions to retrieve your Google Forms.',
            scope: 'https://www.googleapis.com/auth/forms.body.readonly'
        },
        {
            type: 'sensitive',
            isPermissionGiven: false,
            name: 'Permissions to import form responses',
            description: 'To be able to show form responses and build a beautiful responder portal for you, we require permissions to fetch form responses.',
            scope: 'https://www.googleapis.com/auth/forms.responses.readonly'
        }
    ];
    const [defaultContent] = useState<IDefaultContent>({
        type: 'dark',
        permissionText: 'Enable Permissions for Importing\n' + 'Google Forms',
        permissions: googlePermissions
    });


    if (provider === 'google' && (mode === 'readonly' || mode === 'off')) {
        // Nothing to authorize here, so the view can always be closed (the
        // OAUTH_ERROR_VIEW modal passes closable={false} while a connection is due).
        return (
            <div className="relative w-full rounded border border-black-300 bg-white px-6 pb-6 pt-14 md:max-w-[560px]">
                <CloseModal />
                <GoogleFormsImportNotice mode={mode} />
            </div>
        );
    }

    return (
        <div className="relative flex w-full flex-col items-center justify-center space-y-5 rounded border border-black-300 bg-white py-10 text-sm md:max-w-[560px]">
            {closable && <CloseModal />}
            <div className="mt-0! flex flex-col items-center justify-between">
                <CheckedCircle />
                <h2 className="text-black-900 mt-6 whitespace-pre-wrap text-center text-lg font-semibold md:text-xl">{defaultContent.permissionText}</h2>
            </div>

            <div className="w-full border-b">
                {defaultContent.permissions.map((permission, index) => (
                    <Disclosure key={permission.name} defaultOpen={false}>
                        {({ open }) => (
                            <>
                                <Disclosure.Button className="body6 text-black-800 flex w-full items-center justify-between border-t px-8 py-3 text-left font-medium focus:outline-hidden focus-visible:ring focus-visible:ring-blue-500/75">
                                    <span>
                                        {unauthorizedScopes && unauthorizedScopes.length > 0 && <span className={`mr-2 text-lg ${unauthorizedScopes?.includes(permission.scope || '') ? 'text-black-600' : 'text-green-500'}`}>✔</span>}
                                        {permission.name}
                                    </span>
                                    <ChevronDown className={`${open ? 'rotate-180 transform' : ''} h-3 w-3 text-blue-900`} />
                                </Disclosure.Button>
                                <Disclosure.Panel className="body4 -mt-2 w-full pb-2 pr-8 text-gray-500">{<div className="px-8">{permission?.description}</div>}</Disclosure.Panel>
                            </>
                        )}
                    </Disclosure>
                ))}
            </div>
            {provider === 'google' && (
                <p className="text-black-800 px-8 text-left text-[12px] leading-5">
                    Better Collected Platform&apos;s use and transfer to any other app of information received from Google APIs will adhere to{' '}
                    <a target="_blank" rel="noreferrer" className="text-blue-500 hover:text-blue-400" href="https://developers.google.com/terms/api-services-user-data-policy">
                        Google API Services User Data Policy
                    </a>
                    , including the Limited Use requirements.
                </p>
            )}
            <div className="mt-3! flex w-full px-8 items-center space-x-3">
                <Checkbox
                    id="consent"
                    checked={isConsentGiven}
                    onCheckedChange={(checked) => setIsConsentGiven(!!checked)}
                />
                <label
                    htmlFor="consent"
                    className="cursor-pointer"
                >
                    <div className="body4">
                        By signing in, you agree to our{' '}
                        <a href="https://bettercollected.com/terms-of-service/" target="_blank" rel="noreferrer" className="text-blue-500 hover:text-blue-400">
                            Terms of Service
                        </a>{' '}
                        and{' '}
                        <a href="https://bettercollected.com/privacy-policy/" target="_blank" rel="noreferrer" className="text-blue-500 hover:text-blue-400">
                            Privacy Policy
                        </a>
                        .
                    </div>
                </label>
            </div>
            <div className="w-[230px]">
                <ConnectWithProviderButton disabled={!isConsentGiven} type={defaultContent.type} url={`${environments.API_ENDPOINT_HOST}/auth/${provider}/oauth`} text={`Authorize ${_.capitalize(provider)}`} creator />
            </div>
        </div>
    );
}
